import "./App.css";
import { BrowserRouter as BrowserRouterOriginal, HashRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";

const isElectron = window.electron?.isElectron;
const Router = isElectron ? HashRouter : BrowserRouterOriginal;
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import AuthCallback from "./components/AuthCallback";
import AnimatedBackground from "./components/AnimatedBackground";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingSupport from "./components/FloatingSupport";
import InstallPWA from "./components/InstallPWA";

import HomePage from "./pages/HomePage";
import SuppliersPage from "./pages/SuppliersPage";
import AuthPage from "./pages/AuthPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import DashboardPage from "./pages/DashboardPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import WalletPage from "./pages/WalletPage";
import OrdersPage from "./pages/OrdersPage";
import NotificationsPage from "./pages/NotificationsPage";
import ProfilePage from "./pages/ProfilePage";
import StaticPage from "./pages/StaticPage";
import StoresPage from "./pages/StoresPage";
import StoreDetailPage from "./pages/StoreDetailPage";
import StoreChatPage from "./pages/StoreChatPage";
import DirectChatPage from "./pages/DirectChatPage";
import CreateStorePage from "./pages/CreateStorePage";
import SupportPage from "./pages/SupportPage";
import DesapegaPage from "./pages/DesapegaPage";
import BraneCoinsPage from "./pages/BraneCoinsPage";
import AddProductPage from "./pages/AddProductPage";
import SocialPage from "./pages/SocialPage";
import VirtualShoppingBrane from "./pages/VirtualShoppingBrane";
import ShoppingApp from "./pages/VirtualShoppingBrane/shopping/ShoppingApp";
import BraneStudio from "./pages/BraneStudio";
import BraneAgent from "./pages/BraneAgent";
import Notanews from "./pages/Notanews/Notanews";
import AffiliateAgentApp from "./pages/AffiliateAgent/AffiliateAgent";
import PublicStoreFront from "./pages/AffiliateAgent/PublicStoreFront";
import BLivreAdminPage from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminReports from "./pages/admin/AdminReports";
import AdminDenuncias from "./pages/admin/AdminDenuncias";
import AdminFinanceiro from "./pages/admin/AdminFinanceiro";
import AdminSuporte from "./pages/admin/AdminSuporte";
import AdminConfiguracoes from "./pages/admin/AdminConfiguracoes";
import AdminPasswordReset from "./pages/admin/AdminPasswordReset";
import AdminMessages from "./pages/admin/AdminMessages";
import AdminBanners from "./pages/admin/AdminBanners";
import { AdminDataProvider } from "./contexts/AdminDataContext";
import BLivreAuthPage from "./pages/BLivreAuthPage";
import BLivreMessagesPage from "./pages/BLivreMessagesPage";
import { BLivreAuthProvider } from "./contexts/BLivreAuthContext";
import { I18nProvider } from "./i18n/I18nContext";
import BLivreSEO from "./components/BLivreSEO";
import ErrorBoundary from "./components/ErrorBoundary";
import BranpyApp from "./pages/Branpy/BranpyApp";

function BLivreLayout() {
  return (
    <BLivreAuthProvider>
      <BLivreSEO page="home" />
      <Outlet />
    </BLivreAuthProvider>
  );
}
import { Toaster } from "./components/ui/sonner";
import AddStoreProductPage from "./pages/AddStoreProductPage";
import AddDesapegaProductPage from "./pages/AddDesapegaProductPage";
import AdminPage from "./pages/AdminPage";
import PromotionPlansPage from "./pages/PromotionPlansPage";

function ProtectedRoute({ children, adminOnly = false, sellerOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050608]">
        <div className="w-9 h-9 border-2 border-[#8A2CFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/market" replace />;
  }

  if (
    sellerOnly &&
    user.role !== "seller" &&
    user.role !== "admin"
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }

  const path = location.pathname;
  const isAuth = path === "/auth";

  if (isAuth) {
    return (
      <>
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="*" element={<Navigate to="/market" replace />} />
        </Routes>

        <Toaster position="top-right" />
      </>
    );
  }

  const isAdmin = path.startsWith("/admin");
  const isSocial = path.startsWith("/social") || path.startsWith("/blivre") || path.startsWith("/market/blivre") || path.startsWith("/virtualshoppingbrane") || path.startsWith("/brane-studio") || path.startsWith("/brane-agent") || path.startsWith("/agente") || path.startsWith("/shopping") || path.startsWith("/notanews") || path.startsWith("/affiliate-agent") || path.startsWith("/branpy");

  return (
    <>
      <AnimatedBackground />

      <div className="relative z-10">
        {!isAdmin && !isSocial && <Navbar />}

        <main className="min-h-screen">
          <Routes>
            <Route path="/" element={isElectron ? <Navigate to="/branpy/live" replace /> : <Navigate to="/market" replace />} />
            <Route path="/market" element={<HomePage />} />

            <Route path="/fornecedores" element={<SuppliersPage />} />

            <Route path="/products" element={<ErrorBoundary><ProductsPage /></ErrorBoundary>} />
            <Route path="/products/:id" element={<ErrorBoundary><ProductDetailPage /></ErrorBoundary>} />

            <Route path="/stores" element={<ErrorBoundary><StoresPage /></ErrorBoundary>} />

            <Route
              path="/stores/create"
              element={
                <ProtectedRoute>
                  <CreateStorePage />
                </ProtectedRoute>
              }
            />

            <Route path="/stores/:slug" element={<ErrorBoundary><StoreDetailPage /></ErrorBoundary>} />

            <Route
              path="/stores/:slug/chat"
              element={
                <ProtectedRoute>
                  <StoreChatPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chat/:userId"
              element={
                <ProtectedRoute>
                  <DirectChatPage />
                </ProtectedRoute>
              }
            />

            <Route path="/desapega" element={<DesapegaPage />} />

            {/* Admin — painel completo */}
            <Route path="/blivre/admin" element={<ErrorBoundary><AdminDataProvider><BLivreAdminPage /></AdminDataProvider></ErrorBoundary>}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="banners" element={<AdminBanners />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="denuncias" element={<AdminDenuncias />} />
              <Route path="financeiro" element={<AdminFinanceiro />} />
              <Route path="suporte" element={<AdminSuporte />} />
              <Route path="password-reset" element={<AdminPasswordReset />} />
              <Route path="configuracoes" element={<AdminConfiguracoes />} />
            </Route>

            <Route element={<BLivreLayout />}>
              <Route path="/blivre/login" element={<BLivreAuthPage />} />
              <Route path="/blivre/register" element={<BLivreAuthPage />} />
              <Route path="/blivre/messages" element={<BLivreMessagesPage />} />
              <Route path="/blivre" element={<SocialPage />} />
              <Route path="/blivre/*" element={<SocialPage />} />
              <Route path="/social" element={<Navigate to="/blivre" replace />} />
              <Route path="/social/*" element={<Navigate to="/blivre" replace />} />
              <Route path="/market/blivre" element={<Navigate to="/blivre" replace />} />
            </Route>

            <Route path="/virtualshoppingbrane" element={<VirtualShoppingBrane />} />
            <Route path="/brane-studio" element={<Navigate to="/virtualshoppingbrane" replace />} />
            <Route path="/shopping" element={<ShoppingApp onClose={() => window.history.back()} />} />
            <Route path="/brane-studio/shopping" element={<Navigate to="/shopping" replace />} />
            <Route path="/brane-agent" element={<BraneAgent />} />
            <Route path="/agente" element={<BraneAgent />} />
            <Route path="/notanews/*" element={<Notanews />} />
            <Route path="/affiliate-agent/*" element={<AffiliateAgentApp />} />
            <Route path="/branpy/*" element={<BranpyApp />} />
            <Route path="/loja/:categoria" element={<PublicStoreFront />} />

            <Route
              path="/add-product"
              element={
                <ProtectedRoute sellerOnly>
                  <AddProductPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/add-product/feed"
              element={
                <ProtectedRoute sellerOnly>
                  <AddStoreProductPage mode="feed" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/add-product/store"
              element={
                <ProtectedRoute sellerOnly>
                  <AddStoreProductPage mode="store" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/add-product/desapega"
              element={
                <ProtectedRoute sellerOnly>
                  <AddDesapegaProductPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cart"
              element={
                <ProtectedRoute>
                  <CartPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/checkout"
              element={
                <ProtectedRoute>
                  <CheckoutPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/wallet"
              element={
                <ProtectedRoute>
                  <WalletPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <OrdersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <SupportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/brane-coins"
              element={
                <ProtectedRoute>
                  <BraneCoinsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/*"
              element={
                <ProtectedRoute adminOnly>
                  <AdminPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/promote"
              element={
                <ProtectedRoute>
                  <PromotionPlansPage />
                </ProtectedRoute>
              }
            />

            <Route path="/pages/:slug" element={<StaticPage />} />
            <Route path="/terms" element={<Navigate to="/pages/termos" replace />} />
            <Route path="/privacidade" element={<Navigate to="/pages/privacidade" replace />} />

            <Route path="*" element={<Navigate to="/market" replace />} />
          </Routes>
        </main>

        {!isAdmin && !isSocial && <Footer />}
        {!isAdmin && !isSocial && <FloatingSupport />}
        <InstallPWA />
      </div>

      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <ThemeProvider>
          <Router>
            <AppRouter />
          </Router>
        </ThemeProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
